import React, { useRef, useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { PrintTrip } from '../PrintTrip';
import { AvailableTrip } from "./AvailableTrip";
import { useReactToPrint } from 'react-to-print';
import { useHistory } from 'react-router-dom';
import addNotification from 'react-push-notification';
import axios from 'axios'
import useGlobalState, { g_user, API_Root } from "../../globalState";
import logo from '../../favicon.ico';

export const AvailableTrips = () => {
   const [trips, setTrips] = useState([]);
   const [showPrint, setShowPrint] = useState(false);
   const [tripToPrint, setTripToPrint] = useState();
   const [loading, setLoading] = useState(true);
   const [lastUpdated, setLastUpdated] = useState(null);
   const [user,] = useGlobalState(g_user);
   const printRef = useRef(null);
   const toast = useRef(null);
   const history = useHistory();
   const previousCountRef = useRef(null);
   const handlePrint = useReactToPrint({
      content: () => printRef.current,
   });

   const getAvailableTrips = (isManual) => {
      if (!user?.user?.id) {
         return Promise.resolve();
      }
      var apiUrl = `${API_Root}api/trip/GetAvailableTripsForUser?userId=${user.user.id}`;
      return axios.get(apiUrl).then((values) => {
         const nextTrips = values.data || [];
         const nextCount = nextTrips.length;
         if (previousCountRef.current !== null && nextCount > previousCountRef.current) {
            sendNotification(nextCount);
         }
         previousCountRef.current = nextCount;
         setTrips(nextTrips);
         setLastUpdated(new Date());
         setLoading(false);
         if (isManual) {
            toast.current?.show({
               severity: 'success',
               summary: 'List updated',
               detail: nextCount === 1 ? '1 open order' : `${nextCount} open orders`,
               life: 2000
            });
         }
      }).catch(() => {
         setLoading(false);
         toast.current?.show({
            severity: 'error',
            summary: 'Could not load trip orders',
            detail: 'Please try refresh.',
            life: 4000
         });
      });
   }

   useEffect(() => {
      getAvailableTrips(false);
      const interval = setInterval(() => {
         getAvailableTrips(false);
      }, 60000);
      return () => clearInterval(interval);
   }, [user?.user?.id]);

   const sendNotification = (num) => {
      addNotification({
         title: 'Mozo',
         subtitle: 'New trip order available',
         message: num === 1 ? 'There is 1 open trip order' : `There are ${num} open trip orders`,
         theme: 'darkblue',
         icon: { logo },
         native: true
      });
   }

   const printTrip = (t) => {
      setTripToPrint(t);
      setShowPrint(true);
   }

   const orderClaimed = (vehicleLabel, tripId) => {
      toast.current?.show({
         severity: 'success',
         summary: 'Order claimed',
         life: 8000,
         content: (
            <div className="flex flex-column gap-2">
               <div className="font-bold">Order claimed</div>
               <div>{vehicleLabel ? `You claimed the ${vehicleLabel}` : 'You claimed this vehicle'}</div>
               {tripId && (
                  <Button
                     label="View trip"
                     size="small"
                     icon="pi pi-arrow-right"
                     onClick={() => history.push(`/my-trips/${tripId}`)}
                  />
               )}
            </div>
         )
      });
   }

   const orderClaimFailed = () => {
      toast.current?.show({
         severity: 'error',
         summary: 'Could not claim order',
         detail: 'This vehicle may already be taken. Refresh and try again.',
         life: 4000
      });
   }

   const formatUpdated = () => {
      if (!lastUpdated) {
         return '';
      }
      return lastUpdated.toLocaleTimeString('en-US', {
         hour: 'numeric',
         minute: '2-digit'
      });
   }

   const openCount = trips.length;
   const countLabel = openCount === 1 ? '1 open trip order' : `${openCount} open trip orders`;

   return (
      <>
         <Toast ref={toast} />
         <div className="jobs-page">
            <div className="jobs-page-header">
               <div>
                  <h2 className="jobs-page-title">Trip Orders</h2>
                  <p className="jobs-page-meta">
                     {loading ? 'Loading open trip orders…' : countLabel}
                     {lastUpdated && !loading ? ` · Updated ${formatUpdated()}` : ''}
                  </p>
               </div>
               <Button
                  label="Refresh"
                  icon="pi pi-refresh"
                  onClick={() => getAvailableTrips(true)}
                  disabled={loading}
               />
            </div>

            {loading && trips.length === 0 && (
               <div className="jobs-empty">
                  <i className="pi pi-spin pi-spinner text-3xl mb-3"></i>
                  <p>Loading open trip orders…</p>
               </div>
            )}

            {!loading && trips.length === 0 && (
               <div className="jobs-empty">
                  <i className="pi pi-car text-5xl mb-3"></i>
                  <h3>No open trip orders right now</h3>
                  <p>This list updates automatically. Tap Refresh to check again.</p>
               </div>
            )}

            {trips.length > 0 && (
               <div className="jobs-list">
                  {trips.map((item) => (
                     <AvailableTrip
                        key={item.id}
                        trip={item}
                        onChange={() => getAvailableTrips(false)}
                        onClaimed={orderClaimed}
                        onClaimFailed={orderClaimFailed}
                        onPrint={() => printTrip(item)}
                     />
                  ))}
               </div>
            )}
         </div>
         <Dialog header="Print trip sheet" visible={showPrint} style={{ width: '65vw' }} breakpoints={{ '960px': '95vw', '641px': '100vw' }} onHide={() => setShowPrint(false)}>
            <div className="w-full text-right mt-2"><Button className="p-2" label="Print" icon="pi pi-print" onClick={() => handlePrint()} /></div>
            <PrintTrip ref={printRef} trip={tripToPrint}></PrintTrip>
         </Dialog>
      </>
   );
}
